import { ChevronDown, ChevronRight } from "lucide-react";
import React, { useState } from "react";

import { REQUIRED_APP_SCOPE_GROUPS, REQUIRED_APP_SCOPES } from "./datadogScopes";
import { DATADOG_JS_SNIPPET } from "./datadogSnippet";

const copyToClipboard = async (text: string): Promise<void> => {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }

  const textarea = document.createElement("textarea");
  textarea.value = text;
  textarea.style.position = "fixed";
  textarea.style.left = "-9999px";
  textarea.style.top = "-9999px";
  document.body.appendChild(textarea);
  textarea.focus();
  textarea.select();
  document.execCommand("copy");
  document.body.removeChild(textarea);
};

const InstructionDropdown: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="inline-block">
      <button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        className="inline-flex items-center gap-1 text-primary hover:text-primary/80 text-sm"
      >
        {isOpen ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
        {label}
      </button>
      {isOpen ? <div className="mt-2 space-y-2 pl-4 text-sm text-muted-foreground">{children}</div> : null}
    </div>
  );
};

interface DatadogInstructionsProps {
  productName?: string;
}

const DatadogInstructions: React.FC<DatadogInstructionsProps> = ({ productName = "TierZero AI" }) => {
  const [copyStatus, setCopyStatus] = useState<"idle" | "copied" | "error">("idle");

  const handleCopyJsSnippet = async (): Promise<void> => {
    try {
      await copyToClipboard(DATADOG_JS_SNIPPET);
      setCopyStatus("copied");
    } catch (error) {
      console.error("Failed to copy Datadog JS snippet", error);
      setCopyStatus("error");
    } finally {
      setTimeout(() => setCopyStatus("idle"), 10000);
    }
  };

  return (
    <div className="text-muted-foreground space-y-3">
      <div className="mb-4">
        <p className="mb-2 font-bold">API Key Instructions:</p>
        <ol className="text-sm space-y-2 list-decimal list-inside">
          <li>Log into your Datadog account.</li>
          <li>
            Navigate to{" "}
            <span className="text-amber-600">Organization Settings</span>
            {" > "}
            <span className="text-amber-600">API Keys</span> in the main menu.
          </li>
          <li>
            Click on <span className="text-amber-600">New Key</span>.
          </li>
          <li>
            Enter a name for your key (e.g., "{productName} Integration") and click{" "}
            <span className="text-amber-600">Create Key</span>.
          </li>
          <li>
            Copy the generated API Key and paste it into the API Key field
            below.
          </li>
        </ol>
      </div>

      <div className="mb-4">
        <p className="mb-2 font-bold">Application Key Instructions:</p>
        <ol className="text-sm space-y-2 list-decimal list-inside">
          <li>
            Navigate to{" "}
            <span className="text-amber-600">Organization Settings</span>
            {" > "}
            <span className="text-amber-600">Application Keys</span> in the main
            menu.
          </li>
          <li>
            Click on <span className="text-amber-600">New Key</span>.
          </li>
          <li>
            Enter a name for your application key (e.g., "{productName} Integration")
            and click <span className="text-amber-600">Create Key</span>.
            <div className="mt-2">
              <InstructionDropdown label="App Key Scopes">
                <p className="text-xs">
                  If you would like to use App Key scopes, include the following
                  required permissions ({REQUIRED_APP_SCOPES.length} total):
                </p>
                <ul className="list-disc list-inside space-y-1 text-xs">
                  {REQUIRED_APP_SCOPE_GROUPS.map((group) => (
                    <li key={group.label}>
                      <span className="font-semibold text-foreground">
                        {group.label}:
                      </span>{" "}
                      <span className="text-green-400">
                        {group.scopes.join(", ")}
                      </span>
                    </li>
                  ))}
                </ul>
                <div className="rounded border border-border/60 bg-popover/40 p-3 text-xs text-muted-foreground">
                  <p className="mb-2 font-semibold">
                    Note: If you're comfortable using the browser console, you
                    can copy and run the following Javascript snippet to
                    automatically click the checkboxes for you.
                  </p>
                  <button
                    type="button"
                    onClick={handleCopyJsSnippet}
                    className="inline-flex items-center rounded border border-primary px-3 py-1 text-[11px] font-semibold text-primary/80 transition hover:bg-primary/10"
                  >
                    Copy Javascript Snippet
                  </button>
                  {copyStatus === "copied" ? (
                    <span className="ml-2 text-green-400">
                      Copied! Once you have the Datadog "Edit Key Scope" dialog open,
                      paste the snippet into your browser console and press Enter.
                    </span>
                  ) : null}
                  {copyStatus === "error" ? (
                    <span className="ml-2 text-red-400">
                      Copy failed. Please try again.
                    </span>
                  ) : null}
                </div>
              </InstructionDropdown>
            </div>
          </li>
          <li>
            Copy the generated Application Key and paste it into the Application
            Key field below.
          </li>
        </ol>
      </div>
    </div>
  );
};

export default DatadogInstructions;
